const cities = [
  { name: "Miami", zips: "33101–33299" },
  { name: "Miami Beach", zips: "33139–33141" },
  { name: "Coral Gables", zips: "33134" },
  { name: "Aventura", zips: "33160" },
  { name: "Doral", zips: "33122" },
  { name: "Hialeah", zips: "33002–33018" },
  { name: "Miami Gardens", zips: "33054–33056" },
  { name: "Kendall", zips: "33156–33196" },
  { name: "Pinecrest", zips: "33156" },
  { name: "Palmetto Bay", zips: "33157" },
  { name: "Homestead", zips: "33030–33035" },
  { name: "Brickell", zips: "33131" },
  { name: "Wynwood", zips: "33127" },
  { name: "Little Havana", zips: "33125–33135" },
  { name: "Coconut Grove", zips: "33133" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from home or work in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle the rest.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function MiamiLocalAreaSection() {
  return (
    <section id="service-area" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Miami & Miami-Dade County
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Whether you are in Brickell, Wynwood, South Beach, Little Havana, Coconut Grove, Coral Gables, Doral, Hialeah, or anywhere across Miami-Dade County, Fast Passport Center makes expedited passport processing simple — no agency lines, no guessing.
          </p>
        </div>

        {/* Full-width editorial intro */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 md:p-8 mb-12">
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Miami is the cultural and economic capital of South Florida, a city where international travel is not a luxury — it is a way of life. With a population of over 450,000 within city limits and nearly three million across Miami-Dade County, Miami is one of the most internationally connected cities in the United States. Founded in 1896 with just a few hundred residents, Miami has grown into a global metropolis that serves as the primary gateway between the United States and Latin America, the Caribbean, and increasingly, Europe and Asia.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Miami International Airport (MIA) is the busiest airport in Florida for international passengers and ranks among the top ten busiest international airports in the United States. It serves as the primary hub for American Airlines' Latin American and Caribbean operations, with direct flights to more than 100 international destinations across six continents. For Miami residents, MIA is not just an airport — it is a portal to family in Bogota, business meetings in Sao Paulo, weddings in Havana, ski trips to Patagonia, and yacht charters across the Mediterranean.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            PortMiami, located on Dodge Island in Biscayne Bay, is the world's busiest cruise port by passenger volume, handling more than seven million cruise passengers annually. Major cruise lines including Royal Caribbean, Carnival, Norwegian, MSC, Disney, and Celebrity operate from PortMiami, making Caribbean and Bahamas cruises a routine weekend option for Miami families. The proximity between virtually every Miami neighborhood and the cruise terminal — often just fifteen to twenty minutes — means residents book cruises with minimal advance planning, creating a constant stream of last-minute passport discoveries.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Miami's cultural diversity creates unique passport demand patterns. Little Havana, Calle Ocho, and surrounding neighborhoods are home to one of the largest Cuban-American populations in the United States, with families maintaining active connections to Havana, Santiago, and other Cuban cities. Haitian communities in Little Haiti, Nicaraguan enclaves in Sweetwater, Venezuelan professionals in Doral, Colombian families in Kendall, and Brazilian communities throughout Miami-Dade all generate frequent international travel for family visits, cultural events, business, and education. These heritage trips often carry emotional urgency — a sick relative, a family wedding, a long-awaited reunion — and cannot be postponed for standard passport processing.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            The city's neighborhoods each have distinct travel profiles. Brickell and Downtown Miami house a young professional demographic working in finance, technology, law, and international trade — professionals who book international business trips with two to three weeks of notice. Wynwood and the Design District attract creative professionals who travel to Art Basel events in Basel, Hong Kong, and Paris, plus design conferences across Europe and Asia. Coral Gables and Pinecrest serve established families whose children attend international schools and participate in exchange programs. Miami Beach and South Beach draw tourists and hospitality industry workers who transition between local tourism jobs and international seasonal work.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Miami's yachting and marine industry rivals Fort Lauderdale's, with the annual Miami International Boat Show, the Miami Yacht Show, and a robust charter and brokerage ecosystem operating from Biscayne Bay, Key Biscayne, and Coconut Grove marinas. Crew members on private yachts, charter clients, and marine industry professionals require valid passports for international maritime travel — Bahamas day trips, Caribbean island-hopping, trans-Atlantic crossings, and Mediterranean summer seasons. These travelers often discover passport issues at the worst possible moment, when a charter departure is hours away or a boat show in Monaco is days from starting.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left — FPC Office + How It Works */}
          <div>
            {/* FPC Office Card */}
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-building-2-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Delray Beach</p>
                  <p className="text-white/60 text-xs mt-0.5">401 W Atlantic Ave #351, Delray Beach, FL 33444</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Nearest office to Miami — approximately 50 miles north on I-95
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">Courier pickup available throughout Miami-Dade County</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, choose what works best for you: <strong className="text-white">courier pickup</strong> from your home or office in most cases, <strong className="text-white">ship with our prepaid label</strong> from any UPS/FedEx location, or <strong className="text-white">schedule a drop-off</strong> at our Delray Beach office. Most clients ship overnight — no visit required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* How FPC Helps */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Cities We Serve + Trust strip */}
          <div>
            <div className="bg-white rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Cities & Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-[#F7F8FA] rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Miami-Dade County — start online, ship overnight, and we handle the rest.
              </p>
            </div>

            {/* Internal links */}
            <div className="bg-white rounded-lg p-5 mb-6 border border-gray-200">
              <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">Nearby Cities We Serve</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: "Fort Lauderdale", href: "/cities/fort-lauderdale" },
                  { name: "Hollywood", href: "/cities/hollywood" },
                  { name: "Aventura", href: "/cities/aventura" },
                  { name: "Coral Gables", href: "/cities/coral-gables" },
                  { name: "Weston", href: "/cities/weston" },
                  { name: "Boca Raton", href: "/cities/boca-raton" },
                ].map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                  >
                    {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Trust credentials strip */}
            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}